// Theme utility functions

// Fallback themes in case CSS extraction fails
const FALLBACK_THEMES = [
  'purple',
  'ocean',
  'green',
  'navy',
  'burgundy',
  'slate',
  'pink',
  'lavender',
  'coral',
  'copper',
  'rose',
  'midnight',
  'moss',
  'beatsaber'
];

// Cache for extracted themes to avoid repeated DOM queries
let cachedThemes = null;

/**
 * Validates if a theme name is available
 * @param {string} themeName - The theme name to validate
 * @returns {boolean} - True if the theme is available
 */
export const isValidTheme = (themeName) => {
  return extractThemesFromCSS().includes(themeName);
};

/**
 * Gets the theme class name for a given theme
 * @param {string} themeName - The theme name
 * @returns {string} - The CSS class name for the theme
 */
export const getThemeClass = (themeName) => {
  if (isValidTheme(themeName)) {
    return `scheme-${themeName}`;
  }
  return 'scheme-ocean'; // Default fallback
};

export const getSchemeClassNames = () => (
  getAvailableThemes().map((name) => `scheme-${name}`)
);

export const applySchemeClass = (element, themeClass) => {
  if (!element || !themeClass) return;

  getSchemeClassNames().forEach((className) => {
    element.classList.remove(className);
  });
  element.classList.add(themeClass);
};

export const clearSchemeClasses = (element) => {
  if (!element) return;

  getSchemeClassNames().forEach((className) => {
    element.classList.remove(className);
  });
};

export const getActiveSchemeClass = () => {
  const nav = document.querySelector('.nav-background');
  if (nav) {
    const schemeClass = Array.from(nav.classList).find((className) => className.startsWith('scheme-'));
    if (schemeClass) return schemeClass;
  }

  const bodyScheme = Array.from(document.body.classList).find((className) => className.startsWith('scheme-'));
  return bodyScheme || getThemeClass('ocean');
};



/**
 * Extracts available themes from CSS classes
 * @returns {Array} - Array of available theme names
 */
export const extractThemesFromCSS = () => {
  // Return cached themes if available
  if (cachedThemes) {
    return cachedThemes;
  }

  const styleSheets = Array.from(document.styleSheets);
  const themes = [];
  
  styleSheets.forEach(sheet => {
    try {
      const rules = Array.from(sheet.cssRules || sheet.rules);
      rules.forEach(rule => {
        if (rule.selectorText && rule.selectorText.startsWith('.scheme-')) {
          const themeName = rule.selectorText.replace('.scheme-', '');
          if (!themes.includes(themeName)) {
            themes.push(themeName);
          }
        }
      });
    } catch (e) {
      // CORS issues or other errors when accessing external stylesheets
      console.warn('Could not access stylesheet:', e);
    }
  });
  
  // Cache the results
  cachedThemes = themes.length > 0 ? themes : FALLBACK_THEMES;
  return cachedThemes;
};

/**
 * Gets all available themes
 * @returns {Array} - Array of available theme names
 */
export const getAvailableThemes = () => {
  return extractThemesFromCSS();
};

/**
 * Clears the theme cache (useful for development/testing)
 */
export const clearThemeCache = () => {
  cachedThemes = null;
};

const parseCssChannel = (value) => {
  const trimmed = String(value).trim();
  if (trimmed.endsWith('%')) return Number(trimmed.slice(0, -1)) / 100;
  return Number(trimmed);
};

const parseOklabColor = (color) => {
  const match = color.match(/^oklab\(\s*([^\s/]+)\s+([^\s/]+)\s+([^\s/)]+)/i);
  if (!match) return null;
  return {
    L: parseCssChannel(match[1]),
    a: parseCssChannel(match[2]),
    b: parseCssChannel(match[3]),
  };
};

const parseOklchColor = (color) => {
  const match = color.match(/^oklch\(\s*([^\s/]+)\s+([^\s/]+)\s+([^\s/)]+)/i);
  if (!match) return null;
  const L = parseCssChannel(match[1]);
  const C = parseCssChannel(match[2]);
  const H = (Number(match[3]) * Math.PI) / 180;
  return {
    L,
    a: C * Math.cos(H),
    b: C * Math.sin(H),
  };
};

export const colorToHex = (color) => {
  if (!color) return null;

  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    return trimmed.length === 4
      ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
      : trimmed;
  }

  const rgbMatch = trimmed.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgbMatch) {
    const toHex = (value) => Math.round(Number(value)).toString(16).padStart(2, '0');
    return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
  }

  const oklab = parseOklabColor(trimmed) || parseOklchColor(trimmed);
  if (oklab) {
    return rgbToHex(oklabToSrgb(oklab));
  }

  return trimmed;
};

const hexToRgb = (hex) => {
  const normalized = colorToHex(hex)?.replace('#', '');
  if (!normalized || normalized.length !== 6) return null;

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const linearizeSrgb = (value) => (
  value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
);

const delinearizeSrgb = (value) => (
  value <= 0.0031308 ? 12.92 * value : 1.055 * Math.pow(value, 1 / 2.4) - 0.055
);

const rgbToOklab = ({ r, g, b }) => {
  const lr = linearizeSrgb(r / 255);
  const lg = linearizeSrgb(g / 255);
  const lb = linearizeSrgb(b / 255);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  };
};

const oklabToRgb = ({ L, a, b }) => {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  return {
    r: 255 * ( 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: 255 * (-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: 255 * (-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s),
  };
};

const oklabToSrgb = ({ L, a, b }) => {
  const linear = oklabToRgb({ L, a, b });
  return {
    r: 255 * delinearizeSrgb(linear.r / 255),
    g: 255 * delinearizeSrgb(linear.g / 255),
    b: 255 * delinearizeSrgb(linear.b / 255),
  };
};

const rgbToHex = ({ r, g, b }) => {
  const toChannel = (value) => Math.round(Math.min(255, Math.max(0, value)))
    .toString(16)
    .padStart(2, '0');

  return `#${toChannel(r)}${toChannel(g)}${toChannel(b)}`;
};

export const rotateOklabHue = (color, degrees = 180) => {
  const hex = colorToHex(color);
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const oklab = rgbToOklab(rgb);

  return rgbToHex(oklabToRgb({
    L: oklab.L,
    a: (oklab.a * cos) - (oklab.b * sin),
    b: (oklab.a * sin) + (oklab.b * cos),
  }));
};

export const readThemeCssColors = (element) => {
  if (!element || typeof window === 'undefined') return null;

  const styles = window.getComputedStyle(element);
  const read = (name) => colorToHex(styles.getPropertyValue(name));

  return {
    dark: read('--theme-dark'),
    light: read('--theme-light'),
    accent1: read('--theme-accent-1'),
    accent2: read('--theme-accent-2'),
  };
};

export const readThemeColorsForScheme = (schemeName) => {
  if (typeof document === 'undefined') return null;

  const themeClass = getThemeClass(schemeName);
  const probe = document.createElement('div');
  probe.className = themeClass;
  probe.style.display = 'none';
  document.body.appendChild(probe);

  const colors = readThemeCssColors(probe);
  document.body.removeChild(probe);
  return colors;
};

export const getMapThemeColors = (schemeName) => {
  const colors = readThemeColorsForScheme(schemeName) || readThemeColorsForScheme('ocean');
  const base = colors?.dark || '#1a5f7a';
  const background = rotateOklabHue(base, 180);

  return {
    base: background,
    accent: colors?.accent1 || '#2b7a94',
    text: colors?.light || '#e8f4f8',
    background,
  };
}; 